// ============================================================================
// Recherche par similarité — logique commune aux deux plateformes (Vinted et
// Vestiaire) : mêmes sources (image téléversée, produit source, description),
// mêmes règles d'aperçu. Une source de similarité AFFINE un périmètre, elle ne
// compte jamais comme critère qui resserre (règle serveur, identique sur VC).
// ============================================================================

export interface SimilaritySource {
  imageKey: string | null;
  sourceProductId: string | null;
  description: string | null;
}

const stringOrNull = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);

/** Source de similarité d'un filtre stocké (`parsedFilter` Vinted ou `vestiaireFilter` VC). */
export const readSimilaritySource = (filter: Record<string, unknown> | null | undefined): SimilaritySource => ({
  imageKey: stringOrNull(filter?.imageKey),
  sourceProductId: stringOrNull(filter?.sourceProductId),
  description: stringOrNull(filter?.description),
});

/** Le filtre porte-t-il une source de similarité (image, produit source ou description) ? */
export const hasSimilaritySource = (filter: Record<string, unknown> | null | undefined): boolean => {
  const source = readSimilaritySource(filter);
  return source.imageKey != null || source.sourceProductId != null || source.description != null;
};

export interface SimilarityCriteria {
  imageKey?: string;
  description?: string;
}

/**
 * Champs de similarité saisis dans la modale de recherche → critère envoyé (Vinted ou VC,
 * mêmes clés). Image absente ou description vide ⇒ clé absente, jamais une chaîne vide.
 */
export const buildSimilarityCriteria = (
  imageKey: string | null | undefined,
  description: string,
): SimilarityCriteria => {
  const built: SimilarityCriteria = {};
  if (imageKey) built.imageKey = imageKey;
  const trimmed = description.trim();
  if (trimmed) built.description = trimmed;
  return built;
};

/**
 * Préfixe du nombre de l'aperçu : « ≤ » quand la similarité n'est pas encore appliquée (le
 * nombre est un MAJORANT), sinon « + » si le périmètre est tronqué — cf SearchModal.
 */
export const matchPreviewPrefix = ({
  similarityPending,
  capped,
}: {
  similarityPending: boolean;
  capped: boolean;
}): string => (similarityPending ? '≤ ' : capped ? '+' : '');
